import styled from 'foundations-theming/utils';
/* eslint-disable hubspot-dev/no-private-classes */
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useCallback, useState } from 'react';
import InsertMenuContainer from '../components/insertMenu/InsertMenuContainer';
import { editorCapabilitiesCommands, TOOLBAR_CUSTOMIZATION, isInsertCapability, FORMATTING_TRAY, isCustomCapability } from '../editorCapabilities';
import { LAYOUT_TYPES as INTERNAL_LAYOUT_TYPES } from '../editorCapabilities/toolbarCustomization';
import { getTracking } from './configUtils';
import { getDefaultFontPropsWithFallback } from './fontUtils';
import { getGlobalVars } from './globalVarUtil';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { defaultToolbarLayout, defaultFormattingTrayLayout } from '../config/presets/defaultToolbarLayout';
import { inboxLayout, inboxFormattingTrayLayout } from '../config/presets/inboxToolbarLayout';
import { jsx as _jsx } from "react/jsx-runtime";
export const LAYOUT_TYPES = INTERNAL_LAYOUT_TYPES;
export const SmallDivider = styled.div.withConfig({
  displayName: "buildCapabilitiesFromConfig__SmallDivider"
})(["border-right:", " solid ", ";block-size:", ";margin-block:auto;margin-inline:", ";vertical-align:middle;display:inline-block;"], ({
  theme
}) => theme.borderWidth['100'], ({
  theme
}) => theme.color['border-core-disabled'], ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.space['100']);
export const DropdownContentWrapper = styled.div.withConfig({
  displayName: "buildCapabilitiesFromConfig__DropdownContentWrapper"
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  paddingInline: theme.space['200'],
  '@media only screen and (max-width: 544px)': {
    '.size-styles-dropdown': {
      marginTop: `${theme.space['0']} !important`,
      marginLeft: theme.space['100']
    }
  }
}));
export const DropdownWrapper = styled.div.withConfig({
  displayName: "buildCapabilitiesFromConfig__DropdownWrapper"
})(["display:flex;align-items:center;.inbox-text-formatting{width:22px;}@media only screen and (max-width:544px){.private-button--default{width:auto !important;}}"]);
export const SpacerEl = styled.div.withConfig({
  displayName: "buildCapabilitiesFromConfig__SpacerEl"
})(["flex-grow:1;"]);
const DropdownEl = ({
  entry,
  renderComponentForEntry
}) => {
  const [linkedOpen, setLinkedOpen] = useState(false);
  const onOpenChangeHandler = useCallback(evt => setLinkedOpen(evt.target.value), []);
  const renderContent = () => {
    return /*#__PURE__*/_jsx(DropdownContentWrapper, {
      children: entry.children.map(child => renderComponentForEntry(child, true))
    });
  };
  const innerReactElement = /*#__PURE__*/_jsx(Dropdown, {
    buttonText: entry.buttonText,
    "data-test-id": entry.key,
    inlineSize: 'auto',
    paddingInline: "100",
    marginBlock: "0",
    marginInline: "25",
    buttonUse: "link",
    caretRenderer: entry.caretRenderer,
    Content: renderContent,
    minMenuWidth: 0,
    placement: "top right",
    alignSelf: "center",
    onOpenChange: entry.tooltip ? onOpenChangeHandler : undefined
  });
  if (entry.tooltip) {
    return /*#__PURE__*/_jsx(DropdownWrapper, {
      children: /*#__PURE__*/_jsx(Tooltip, {
        title: entry.tooltip,
        open: linkedOpen ? false : undefined,
        children: innerReactElement
      })
    });
  } else {
    return /*#__PURE__*/_jsx(DropdownWrapper, {
      children: innerReactElement
    });
  }
};
function renderToolbar(editorConfig, toolbarLayout) {
  const globalVars = getGlobalVars(editorConfig);
  const tracking = getTracking(editorConfig);
  const toolbar = [];
  const hasChildren = entry => entry.children && entry.children.some(child => editorConfig[child]);
  const renderComponentForEntry = (entry, isChild, extraProps) => {
    if (typeof entry === 'string') {
      if (editorConfig[entry]) {
        var _editorConfig$entry;
        // Custom capabilities with `command` render inline in the toolbar.
        // This check must run before the insert filter below because
        // isInsertCapability() returns true for all CUSTOM_ capabilities.
        if (isCustomCapability(entry) && (_editorConfig$entry = editorConfig[entry]) !== null && _editorConfig$entry !== void 0 && _editorConfig$entry.command) {
          const CustomCapabilityComp = editorConfig[entry].command;
          return /*#__PURE__*/_jsx(CustomCapabilityComp, Object.assign({}, editorConfig[entry], globalVars, getDefaultFontPropsWithFallback(editorConfig), extraProps), entry);
        }
        if (isInsertCapability(entry) && globalVars.useInsertMenu && !isChild) {
          return undefined;
        }
        const CapabilityComp = editorCapabilitiesCommands[entry];
        return /*#__PURE__*/_jsx(CapabilityComp, Object.assign({}, editorConfig[entry], globalVars, getDefaultFontPropsWithFallback(editorConfig), extraProps), entry);
      }
    } else if (typeof entry === 'function') {
      const CustomCommand = entry;
      return /*#__PURE__*/_jsx(CustomCommand, {});
    } else if (typeof entry === 'object' && 'component' in entry) {
      const CustomComponent = entry.component;
      return /*#__PURE__*/_jsx(CustomComponent, {}, entry.key);
    } else {
      // layout helpers
      switch (entry.layoutType) {
        case LAYOUT_TYPES.SPACER:
          return /*#__PURE__*/_jsx(SpacerEl, {
            "data-test-id": "spacer"
          }, entry.key);
        case LAYOUT_TYPES.DROPDOWN:
          {
            return hasChildren(entry) ? /*#__PURE__*/_jsx(DropdownEl, {
              renderComponentForEntry: renderComponentForEntry,
              entry: entry
            }, entry.key) : undefined;
          }
        case LAYOUT_TYPES.DIVIDER:
          {
            return (
              /*#__PURE__*/
              // @bend-expect trellis-tokenized
              _jsx(SmallDivider, {
                style: entry.style,
                "data-test-id": "divider"
              }, entry.key)
            );
          }
        case LAYOUT_TYPES.INSERT_MENU:
          {
            if (!globalVars.useInsertMenu) {
              if (process.env.NODE_ENV !== 'production' && hasChildren(entry)) {
                console.warn('[hubspot-prosemirror] TOOLBAR_CUSTOMIZATION includes an INSERT_MENU entry, but useInsertMenu is false.' + ' Insert capabilities in this menu (e.g. SNIPPETS, MEETINGS, TEMPLATES) will not render.' + ' For non-EMAIL interaction types, include insert capabilities as top-level toolbar entries instead.');
              }
              return undefined;
            }
            if (!hasChildren(entry)) {
              return undefined;
            }
            const insertMenuChildren = entry.children.filter(isInsertCapability);
            return /*#__PURE__*/_jsx(InsertMenuContainer, {
              editorConfig: editorConfig,
              insertTypeOrder: insertMenuChildren,
              buttonContent: entry.buttonContent,
              onOpen: () => tracking.onOpenInsertPopover && tracking.onOpenInsertPopover()
            }, entry.key);
          }
        default:
          break;
      }
    }
    return undefined;
  };
  for (const layoutEntry of toolbarLayout) {
    toolbar.push(renderComponentForEntry(layoutEntry));
  }
  return toolbar.filter(Boolean);
}
export function buildToolbarFromConfig(editorConfig, providedToolbarLayout) {
  const globalVars = getGlobalVars(editorConfig);
  const baseLayout = globalVars.useInboxLayout ? inboxLayout : defaultToolbarLayout;
  const toolbarLayout = editorConfig[TOOLBAR_CUSTOMIZATION] || providedToolbarLayout || baseLayout;
  return renderToolbar(editorConfig, toolbarLayout);
}
export function buildFormattingTrayFromConfig(editorConfig, providedToolbarLayout) {
  const globalVars = getGlobalVars(editorConfig);
  const baseLayout = globalVars.useInboxLayout ? inboxFormattingTrayLayout : defaultFormattingTrayLayout;
  const trayConfig = editorConfig[FORMATTING_TRAY];
  const toolbarLayout = Array.isArray(trayConfig) ? trayConfig : (trayConfig === null || trayConfig === void 0 ? void 0 : trayConfig.layout) || providedToolbarLayout || baseLayout;
  if (!toolbarLayout) {
    return [];
  }
  return renderToolbar(editorConfig, toolbarLayout);
}
export const buildCapabilitiesFromConfig = buildToolbarFromConfig;